import React, { FC, MutableRefObject } from 'react';
import classNames from 'classnames';
import { makeStyles } from 'tss-react/mui';

import CircleIcon from '@mui/icons-material/Circle';
import HourglassEmptyRounded from '@mui/icons-material/HourglassEmptyRounded';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { transactionsTypes } from 'companion-app-components/flux/transactions';
import { tracker } from 'companion-app-components/utils/core';

import { isC2REnabledAccount } from 'data/accounts/utils';
import QHelp from 'components/QHelp';
import QIconButton from 'components/QIconButton';
import useQPreferences from 'components/QPreferences/useQPreferences';

import compose from 'utils/compose';

import { isUnacceptedScheduledTxn, isBankOwnedPending, shouldShowReviewField, shouldDisableReviewField } from 'data/transactions/utils';

import { RegListItem, styles } from '../styles';

import type { AttributesType, CallbacksType } from '../types';

const useStyles = makeStyles()(styles as Record<string, any>);

interface ReviewedFieldProps {
  attributes: AttributesType;
  SHOW_ALL: boolean;
  forceStatic: boolean;
  txn: transactionsTypes.CashFlowTransaction;
  callbacks: CallbacksType;
  isWindowsDataset: boolean;
  fieldActive: boolean;
  isTypeGrossPaycheck: boolean;
  showTooltip: boolean;
  updateShowTooltip: (flag: boolean) => void;
  scriptId: string;
  field: string;
  isAccountNotSynced: boolean;
  editable: boolean;
  showQHelp: (type: string) => void;
  focusField: MutableRefObject<HTMLAnchorElement | null>;
}

const ReviewedField: FC<ReviewedFieldProps> = ({
  attributes,
  SHOW_ALL,
  txn,
  forceStatic,
  callbacks,
  isWindowsDataset, 
  fieldActive,
  isTypeGrossPaycheck,
  showTooltip, 
  updateShowTooltip, 
  scriptId,
  field,
  isAccountNotSynced,
  editable,
  showQHelp,
  focusField,
}) => {
  const { classes } = useStyles();
  const { userPreferences, setUserPreference } = useQPreferences();

  const renderBankPendingState = () => {
    const title = isWindowsDataset && userPreferences.windowsReviewedWarningEnabled ?
      'Click here to learn what this is and how to use it' : 'Pending Transaction';
    return (
      <QIconButton
        aria-label="Bank Pending Transaction"
        focusRipple
        IconComponent={HourglassEmptyRounded}
        IconProps={{ className: classes.bankPending }}
        size="small-target"
        tooltip={title}
      />
    );
  };

  const renderReviewedState = () => {

    let title;
    if (isWindowsDataset) {

      if (userPreferences.windowsReviewedWarningEnabled) {
        title = 'Click here to learn what this is and how to use it';
      } else {
        title = txn.isReviewed
          ? "Transaction 'Accepted' (Learn more in Transactions Settings -> Show/Hide Columns)"
          : "Transaction 'Not Accepted', click to mark 'Accepted' (Learn more in Transactions Settings -> Show/Hide Columns)";
      }
    } else {
      title = txn.isReviewed
        ? "Transaction marked 'reviewed', click to mark 'not reviewed"
        : "Transaction marked 'Not reviewed', click to mark 'reviewed'";
    }

    return (
      <QIconButton
        aria-label="Set Reviewed State"
        ref={fieldActive ?
          (input) => {
            focusField.current = input; // eslint-disable-line
          } : null}
        disabled={forceStatic || isTypeGrossPaycheck}
        autoFocus={fieldActive}
        focusRipple
        IconComponent={txn.isReviewed ? RadioButtonUncheckedIcon : CircleIcon}
        IconProps={{ className: classNames(classes.reviewed, txn.isReviewed ? 'reviewed' : 'not-reviewed') }}
        size="small-target"
        onMouseEnter={() => updateShowTooltip(true)}
        onMouseLeave={() => updateShowTooltip(false)}
        tooltip={title}
        TooltipProps={{ 
          open: showTooltip, 
          position: 'left', 
          disableInteractive: true,
          leaveDelay: 3,
        }}
      />
    );
  };

  let ReviewedComponent;

  if (isBankOwnedPending(txn)) {
    ReviewedComponent = (
      <RegListItem
        {...attributes}
        tabIndex={null}
        className={classNames(classes.iconButtonColumn)}
      >
        {renderBankPendingState()}
      </RegListItem>
    );
  } else if (!shouldShowReviewField(txn.accountId) || isUnacceptedScheduledTxn(txn)) {
    ReviewedComponent = (
      <RegListItem
        {...attributes}
        tabIndex={null}
        className={classNames(classes.iconButtonColumn, forceStatic ? 'nopoint' : '')}
      >
      </RegListItem>
    );
  } else {
    ReviewedComponent = (
      <RegListItem
        {...attributes}
        id={`${scriptId}-${field}-${txn.isReviewed ? 'yes' : 'no'}_`}
        tabIndex={null}
        onClick={shouldDisableReviewField(txn) || forceStatic || isTypeGrossPaycheck || !editable || isAccountNotSynced ? null : () => {
          if (userPreferences.windowsReviewedWarningEnabled) {
            setUserPreference({ windowsReviewedWarningEnabled: false });
            return showQHelp('windowsReviewed');
          }

          if (!txn.isReviewed) {
            tracker.track(tracker.events.txnReview, {
              isC2REnabled: isWindowsDataset ? isC2REnabledAccount(txn.accountId) : false,
              action: 'markAsReviewed',
              count: 1,
            });
          }

          return callbacks.handleMenu?.('reviewed', txn);
        }}
        className={classNames(classes.iconButtonColumn, forceStatic ? 'nopoint' : '')}
      >
        {SHOW_ALL && renderReviewedState()}
      </RegListItem>
    );
  }
  return ReviewedComponent;
};

export default compose(
  QHelp(),
)(ReviewedField);
